import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  CircularProgress,
  LinearProgress,
  IconButton,
} from '@mui/material';
import { AutoAwesome, Psychology, Search, CheckCircle, Close } from '@mui/icons-material';

interface AutoCompleteDialogProps {
  open: boolean;
}

const AutoCompleteDialog: React.FC<AutoCompleteDialogProps> = ({ open }) => {
  // COM-637 (2026-08-15, product decision): a source can hang indefinitely
  // without ever reaching a terminal status (a container restart mid
  // processing, a status write that itself fails) - the backend's watch-list
  // model (discoveryRun.ts) bounds most of that, but this button is the
  // user's own escape hatch regardless. Dismissal is purely local UI state:
  // it never touches the socket, so it cannot cancel or otherwise affect the
  // backend run - closing this dialog just stops showing it. Reset whenever
  // a new run starts (`open` flips false -> true) so dismissing one run's
  // dialog doesn't silently suppress the next run's too.
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    if (open) {
      setDismissed(false);
    }
  }, [open]);

  const isOpen = open && !dismissed;

  return (
    <Dialog
      open={isOpen}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          background: '#1565c0',
          color: 'white',
          position: 'relative',
        }
      }}
    >
      <IconButton
        aria-label="Close"
        onClick={() => setDismissed(true)}
        sx={{
          position: 'absolute',
          top: 8,
          right: 8,
          color: 'white',
        }}
      >
        <Close />
      </IconButton>
      <DialogContent sx={{ p: 4, textAlign: 'center' }}>
        <Box sx={{ mb: 3 }}>
          <Typography variant="h4" sx={{ mb: 1, fontWeight: 'bold' }}>
            Project Setup
          </Typography>
          <Typography variant="body1" sx={{ opacity: 0.9 }}>
            We are automatically configuring your project for regulatory monitoring
          </Typography>
        </Box>

        <Box sx={{ mb: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, justifyContent: 'center' }}>
            <Psychology 
              sx={{ 
                mr: 1, 
                color: '#4CAF50',
                animation: 'fadeInOut 2s ease-in-out infinite',
                '@keyframes fadeInOut': {
                  '0%, 100%': { opacity: 0.6 },
                  '50%': { opacity: 1 },
                },
              }} 
            />
            <Typography variant="body2" sx={{ opacity: 0.8 }}>
              Analyzing your company profile...
            </Typography>
          </Box>
          
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, justifyContent: 'center' }}>
            <Search 
              sx={{ 
                mr: 1, 
                color: '#2196F3',
                animation: 'fadeInOut 2s ease-in-out infinite 0.7s',
                '@keyframes fadeInOut': {
                  '0%, 100%': { opacity: 0.6 },
                  '50%': { opacity: 1 },
                },
              }} 
            />
            <Typography variant="body2" sx={{ opacity: 0.8 }}>
              Searching for relevant information sources...
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, justifyContent: 'center' }}>
            <CheckCircle
              sx={{
                mr: 1,
                color: '#FF9800',
                animation: 'fadeInOut 2s ease-in-out infinite 1.4s',
                '@keyframes fadeInOut': {
                  '0%, 100%': { opacity: 0.6 },
                  '50%': { opacity: 1 },
                },
              }}
            />
            <Typography variant="body2" sx={{ opacity: 0.8 }}>
              Validating and filtering sources...
            </Typography>
          </Box>
        </Box>

        <LinearProgress
          sx={{
            height: 6,
            borderRadius: 3,
            backgroundColor: 'rgba(255, 255, 255, 0.2)',
            '& .MuiLinearProgress-bar': {
              borderRadius: 3,
              background: 'linear-gradient(90deg, #FFD700, #FFA500)',
            }
          }}
        />

        <Typography variant="body2" sx={{ mt: 2, opacity: 0.7 }}>
          This may take a few minutes...
        </Typography>
      </DialogContent>
    </Dialog>
  );
};

export default AutoCompleteDialog; 